/*
 * Round 15 (2026-09-30), Varun item 6: what the Close Friends card of the Slot Stats page shows.
 *
 * SERVER CONTRACT (r15/server @ 568a202, CloseFriendsStats.View.asMap, UiApiSlotStatsController):
 *   GET /uiapi/slots/{slot}/close-friends ->
 *     { slotName, addNewFollowers (716), backfill (717, only true while 716 is), perSessionMin / perSessionMax
 *       (718 / 719 in effect), perDayMin / perDayMax (722 / 720), cap (721), settingsNotes [..], todayLimit,
 *       addedToday, totalAdded, listSize, listSizeAt, listFullAt, alertAt ("yyyy-MM-dd HH:mm", slot time),
 *       capReached, queued, lastReport ("yyyy-MM-dd HH:mm <the worker's line>"), notRunningReason,
 *       state: OFF | NOT_RUNNING | LIST_FULL | CAP_REACHED | DAY_LIMIT_REACHED | ADDING, stateText,
 *       history (the 90-day CSV, newest first), yesterday, last7, last30, last90, daysRecorded }
 *     404 {error:'not_enabled'} while -Dnecro.server.closeFriends is OFF (the default until the live session
 *     passes - O9); a server before round 15 has no such route (404 / 405 without a code).
 *   GET /uiapi/slots/{slot}/stats adds, only while that switch is ON: closeFriendsAddedThisDaySticky,
 *     closeFriendsAddedInPreviousDays (CSV, newest first), totalCloseFriendsAdded. They roll at the SAME day
 *     change as the action counters (CloseFriendsStats.rollDay inside saveSessionStats' day change), so the
 *     day-by-day chart is dated from lastResetDateTimeDays exactly like the actions (lib/slotHistory.js F4).
 *
 * NOT an action type: Instagram sends no notification for a Close Friends edit, so a follower cannot come from
 * one. The server keeps it out of the V1 ratios (RATIO_ACTIONS); here it is NOT in slotHistory METRICS, so it
 * is never in the "Actions done" stack, its totals, the daily table or the actions-done ratios.
 *
 * null = "not recorded" (a slot that never sent a key keeps every column NULL); never shown as 0.
 * Every text a customer reads comes from the server (stateText, the V1 wording) except the short state names
 * of CF_STATES and one state the server has no code for: Instagram limiting the action. The server keeps no
 * "blocked" column (it raises one alert, "Close Friends: Instagram limited this action ('...') - paused until
 * tomorrow; nothing to do"); the worker pauses Close Friends until the next day + 1-6 h and says so in its report
 * line (the LAST report is the one the server keeps):
 *   - the session that hits the block:         ", blocked '<Instagram's text>'"
 *   - every later session while the pause holds (worker r15/integrated, 2026-09-30):
 *                                               ", paused until <ISO local datetime> (blocked '<Instagram's text>')"
 *                                               or ", paused until <ISO local datetime>" when the text is unknown
 *     (a report never has both). The datetime is the slot's local time, "2026-09-30T03:12:00" (Java's
 *     LocalDateTime.toString() drops ":00" seconds, so "2026-09-30T03:12" is read too).
 * The card shows "Instagram limited" when today's last report says "blocked '...'" (the resume time is not in
 * that line: "paused until tomorrow"), or when the last report of ANY day says "paused until <ts>" and ts is still
 * ahead ("paused until 30 Sep, 03:12"). Once ts has passed it shows the server's state again. A "paused until"
 * whose time cannot be read is ignored, with one console warning (pausedUntil).
 */
import { dailySeries, parseHistoryCsv, isoDay, dayLabel, shiftDay, daysBetween } from './slotHistory';
import { actionBars, WEEKLY_FROM_RANGE, fmtDay, fmtInt } from './slotStatsView';

/** The chart colour (stats.css --viz-cf). */
export const CF_COLOR = 'var(--viz-cf)';
/** The key of the one series (never a METRICS key). */
export const CF_KEY = 'closeFriends';

/**
 * Short names for the server's state codes (the full sentence is always the server's stateText) and a tone for
 * the chip: ok (adding), info (today's limit), warn (stopped until the customer acts / Instagram limited),
 * muted (not running / off). LIMITED is this page's own (see the header): its label carries the resume time when the
 * report gives one (cfState), else "tomorrow".
 */
export const CF_STATES = {
  ADDING: { label: 'Adding', tone: 'ok' },
  DAY_LIMIT_REACHED: { label: "Today's limit reached", tone: 'info' },
  CAP_REACHED: { label: 'Cap reached', tone: 'warn' },
  LIST_FULL: { label: 'Instagram says the list is full', tone: 'warn' },
  LIMITED: { label: 'Instagram limited – paused until tomorrow', tone: 'warn' },
  NOT_RUNNING: { label: 'Not running', tone: 'muted' },
  OFF: { label: 'Off', tone: 'muted' },
};

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);

/** A whole count, or null (null / '' / not a number = "not recorded"). */
export function num(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const str = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());

/**
 * The /close-friends body -> what the card reads, or null when it is not that body (an older server's page, an
 * empty answer): the card is then not drawn.
 */
export function normaliseCloseFriends(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!has(raw, 'state') && !has(raw, 'addNewFollowers')) return null;
  const state = str(raw.state);
  return {
    slotName: str(raw.slotName),
    addNewFollowers: raw.addNewFollowers === true,
    backfill: raw.backfill === true,
    perSessionMin: num(raw.perSessionMin),
    perSessionMax: num(raw.perSessionMax),
    perDayMin: num(raw.perDayMin),
    perDayMax: num(raw.perDayMax),
    cap: num(raw.cap),
    todayLimit: num(raw.todayLimit),
    addedToday: num(raw.addedToday),
    totalAdded: num(raw.totalAdded),
    listSize: num(raw.listSize),
    listSizeAt: str(raw.listSizeAt),
    listFullAt: str(raw.listFullAt),
    alertAt: str(raw.alertAt),
    capReached: raw.capReached === true,
    queued: num(raw.queued),
    lastReport: str(raw.lastReport),
    notRunningReason: str(raw.notRunningReason),
    state: state ? state.toUpperCase() : (raw.addNewFollowers === true ? null : 'OFF'),
    stateText: str(raw.stateText),
    history: str(raw.history),
    yesterday: num(raw.yesterday),
    last7: num(raw.last7),
    last30: num(raw.last30),
    last90: num(raw.last90),
    daysRecorded: num(raw.daysRecorded),
    settingsNotes: Array.isArray(raw.settingsNotes) ? raw.settingsNotes.map(str).filter(Boolean) : [],
  };
}

/**
 * A stored report line -> { at: 'yyyy-MM-dd HH:mm' | null, day: 'YYYY-MM-DD' | null, zone: string | null, text }.
 * Close Friends: "2026-09-29 11:40 new 4, seed 0, ..."; saved collection: "2026-09-29 11:42 (Asia/Kolkata) '...': ...".
 * A line without the time prefix comes back whole as text.
 */
export function splitStamp(report) {
  const s = str(report);
  if (!s) return null;
  const m = s.match(/^(\d{4}-\d{1,2}-\d{1,2}[ T]\d{1,2}:\d{2})(?:\s+\(([^)]*)\))?\s*([\s\S]*)$/);
  if (!m) return { at: null, day: null, zone: null, text: s };
  return { at: m[1].replace('T', ' '), day: isoDay(m[1]), zone: str(m[2]), text: m[3].trim() };
}

/**
 * Instagram's text when the LAST report is from `today` and says the action was blocked (the worker's
 * ", blocked '<text>'" of the session that hit the block), else null.
 */
export function limitedToday(cf, today) {
  if (!cf || !cf.lastReport) return null;
  const r = splitStamp(cf.lastReport);
  if (!r || !r.day || r.day !== today) return null;
  const m = r.text.match(/(?:^|,\s*)blocked '([\s\S]*?)'(?=\s*,\s|\s*$)/);
  return m ? m[1].trim() || 'no text' : null;
}

const warnedPause = new Set();
function warnPauseOnce(report, why) {
  if (warnedPause.has(report)) return;
  warnedPause.add(report);
  // eslint-disable-next-line no-console
  console.warn(`[R15-UI] Close Friends: the last report's "paused until" is ignored (${why}): ${report.slice(0, 300)}`);
}

/** "2026-09-30T03:12:00" / "2026-09-30T03:12" (a blank instead of the T too) -> a local Date, or null. */
export function parseLocalDateTime(ts) {
  const m = String(ts || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const [y, mo, d, h, mi, sec] = m.slice(1).map((v) => (v === undefined ? 0 : Number(v)));
  if (h > 23 || mi > 59 || sec > 59) return null;
  const t = new Date(y, mo - 1, d, h, mi, sec);
  if (t.getFullYear() !== y || t.getMonth() !== mo - 1 || t.getDate() !== d) return null;
  return t;
}

/**
 * The pause the LAST report announces (", paused until <ts> (blocked '<text>')" or ", paused until <ts>"), any day:
 * { until: Date, ts: string, text: string|null } - or null when the report announces none, or when its time cannot
 * be read (then one console warning). Whether it is still ahead is cfState's question.
 */
export function pausedUntil(cf) {
  if (!cf || !cf.lastReport) return null;
  const r = splitStamp(cf.lastReport);
  const text = r ? r.text : cf.lastReport;
  if (!/(?:^|,\s*)paused until\b/.test(text)) return null;
  const m = text.match(/(?:^|,\s*)paused until ([^\s,()]+)(?:\s+\(blocked '([\s\S]*?)'\))?(?=\s*,\s|\s*$)/);
  if (!m) {
    warnPauseOnce(cf.lastReport, 'not in the "paused until <time>" form');
    return null;
  }
  const until = parseLocalDateTime(m[1]);
  if (!until) {
    warnPauseOnce(cf.lastReport, `"${m[1]}" is not a local date-time`);
    return null;
  }
  const said = m[2] === undefined ? null : m[2].trim();
  return { until, ts: m[1], text: said || null };
}

/** A resume time as "30 Sep, 03:12". */
export function fmtResume(d) {
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${fmtDay(iso)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/*
 * The last report as a sentence a customer reads (round 15b, 2026-10-01). The worker's line (BC r15i SR2.r15CfReport):
 *   "new N, seed N, backfill N, 460 N, already N, not found N, skipped N, list N (HH:mm) | list not read,
 *    queue N | queue unknown, harvest N, persist ok | persist? | persist unknown (no header) | persist not checked |
 *    persist: a tick did not survive leaving without Done[, queued now N][, dropped N (queue full)]
 *    [, list full[ ('<text>')]][, blocked '<text>' | , paused until <ts>[ (blocked '<text>')]]
 *    [, left without Done (<why>)][, mass change][, ticked N, confirmed by the list size (Done not verified)]
 *    [, ticked N, unconfirmed (no verified Done)][, backfill reach used up]"
 * -> "Added 2 (backfill) · list 2,418 · 47 waiting · Instagram limited until 30 Sep, 03:12".
 * Zero counts, "harvest" and "persist ok" are left out of the sentence; a part this page does not know is kept as the
 * worker wrote it (one console warning). A line with no known part at all is not read (null, one console warning):
 * the card then shows the line as it is. The raw line always stays in the card's "details" disclosure.
 */
const ADD_SOURCES = [['new', 'new followers'], ['seed', 'earlier followers'], ['backfill', 'backfill'], ['460', 'Close Friending targets']];

/** Split at ", " outside (...) and outside '...' (a quote opens after a blank / "(" and closes before ")" / "," / the end). */
export function splitReportParts(text) {
  const s = String(text || '');
  const parts = [];
  let depth = 0;
  let quoted = false;
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "'") {
      if (!quoted && (i === 0 || /[\s(]/.test(s[i - 1]))) quoted = true;
      else if (quoted && /^\s*(?:[),]|$)/.test(s.slice(i + 1))) quoted = false;
    } else if (!quoted && c === '(') depth += 1;
    else if (!quoted && c === ')' && depth > 0) depth -= 1;
    if (!quoted && depth === 0 && c === ',' && /\s/.test(s[i + 1] || '')) {
      parts.push(cur.trim());
      cur = '';
    } else cur += c;
  }
  parts.push(cur.trim());
  return parts.filter(Boolean);
}

const count = (v) => {
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

const warnedReport = new Set();
function warnReportOnce(key, message) {
  if (warnedReport.has(key)) return;
  warnedReport.add(key);
  // eslint-disable-next-line no-console
  console.warn(message);
}

/**
 * The worker's report text (after splitStamp's time) -> { pieces: string[], unknown: string[] } for the sentence
 * (joined by " · " on the card), or null when not one part of it is known (then one console warning).
 */
export function readCfReport(text) {
  const raw = str(text);
  if (!raw) return null;
  const segs = splitReportParts(raw);
  // "ticked N, confirmed by ..." / "ticked N, unconfirmed (...)" is one extra with a ", " of its own
  for (let i = 0; i + 1 < segs.length; i++) {
    if (/^ticked \d[\d,]*$/.test(segs[i]) && /^(?:confirmed by|unconfirmed)\b/.test(segs[i + 1])) {
      segs.splice(i, 2, `${segs[i]}, ${segs[i + 1]}`);
    }
  }
  const pieces = [];
  const unknown = [];
  const added = {};
  let addedAt = -1;
  let known = 0;
  segs.forEach((seg) => {
    let m;
    const src = ADD_SOURCES.find(([k]) => new RegExp(`^${k} (\\d[\\d,]*)$`).test(seg));
    if (src) {
      added[src[0]] = count(seg.slice(src[0].length + 1));
      if (addedAt < 0) { addedAt = pieces.length; pieces.push(null); }
      known += 1;
      return;
    }
    known += 1;
    if ((m = seg.match(/^(already|not found|skipped) (\d[\d,]*)$/))) {
      const n = count(m[2]);
      if (n > 0) pieces.push(m[1] === 'already' ? `${fmtInt(n)} already on the list` : `${fmtInt(n)} ${m[1]}`);
    } else if ((m = seg.match(/^list full(?: \('([\s\S]*)'\))?$/))) {
      pieces.push(`Instagram says the list is full${m[1] ? ` ('${m[1]}')` : ''}`);
    } else if (seg === 'list not read') {
      pieces.push('list not read');
    } else if ((m = seg.match(/^list (\d[\d,]*)(?: \([^)]*\))?$/))) {
      pieces.push(`list ${fmtInt(count(m[1]))}`);
    } else if ((m = seg.match(/^queue (\d[\d,]*|unknown)$/))) {
      pieces.push(m[1] === 'unknown' ? 'queue not read' : `${fmtInt(count(m[1]))} waiting`);
    } else if ((m = seg.match(/^queued now (\d[\d,]*)$/))) {
      if (count(m[1]) > 0) pieces.push(`${fmtInt(count(m[1]))} newly queued`);
    } else if ((m = seg.match(/^dropped (\d[\d,]*) \(queue full\)$/))) {
      if (count(m[1]) > 0) pieces.push(`${fmtInt(count(m[1]))} not queued (the queue is full)`);
    } else if (/^harvest \d[\d,]*$/.test(seg) || /^persist (?:ok|not checked|unknown\b)/.test(seg)) {
      // left out of the sentence (the raw line keeps them)
    } else if (seg === 'persist?') {
      pieces.push('the list is smaller than expected');
    } else if (/^persist: /.test(seg)) {
      pieces.push('a tick was not saved');
    } else if (/^blocked '[\s\S]*'$/.test(seg)) {
      pieces.push('Instagram limited until tomorrow');
    } else if ((m = seg.match(/^paused until (\S+?)(?: \(blocked '[\s\S]*'\))?$/)) && parseLocalDateTime(m[1])) {
      pieces.push(`Instagram limited until ${fmtResume(parseLocalDateTime(m[1]))}`);
    } else if ((m = seg.match(/^left without Done \(([\s\S]*)\)$/))) {
      pieces.push(`closed the list without saving (${m[1]})`);
    } else if (seg === 'mass change') {
      pieces.push('stopped: several rows changed at once');
    } else if ((m = seg.match(/^ticked (\d[\d,]*), (confirmed|unconfirmed)/))) {
      pieces.push(`${fmtInt(count(m[1]))} ticked, ${m[2] === 'confirmed' ? 'confirmed by the list size' : 'not confirmed'}`);
    } else if (seg === 'backfill reach used up') {
      pieces.push('backfill went as far back as it can');
    } else {
      known -= 1;
      unknown.push(seg);
      pieces.push(seg);
    }
  });
  if (!known) {
    warnReportOnce(raw, `[R15-UI] Close Friends: the last report is not in the worker's form, shown as it is: ${raw.slice(0, 300)}`);
    return null;
  }
  if (addedAt >= 0) {
    const srcs = ADD_SOURCES.filter(([k]) => added[k] > 0);
    const total = ADD_SOURCES.reduce((a, [k]) => a + (added[k] || 0), 0);
    let s = 'None added';
    if (srcs.length === 1) s = `Added ${fmtInt(total)} (${srcs[0][1]})`;
    else if (srcs.length > 1) s = `Added ${fmtInt(total)} (${srcs.map(([k, l]) => `${fmtInt(added[k])} ${l}`).join(', ')})`;
    pieces[addedAt] = s;
  }
  if (unknown.length) {
    warnReportOnce(`${raw}\u0000unknown`, `[R15-UI] Close Friends: ${unknown.length} part(s) of the last report not known, kept as written: `
      + `${unknown.map((u) => `"${u.slice(0, 80)}"`).join(', ')}`);
  }
  return { pieces: pieces.filter(Boolean), unknown };
}

/**
 * The state the card shows: the server's, except that Instagram limiting the action (see the header) is shown over
 * "adding" / "today's limit reached" - the customer's own stops (list full, cap, not running, off) always win.
 * @param now the viewer's clock (a Date); the report's times are the slot's local time, as every time on the page
 * @returns {{ code, label, tone, text, until?: Date }}
 */
export function cfState(cf, now = new Date()) {
  const code = cf && cf.state ? cf.state : 'ADDING';
  const serverState = () => {
    const known = CF_STATES[code] || { label: code, tone: 'muted' };
    return { code, ...known, text: cf ? cf.stateText : null };
  };
  if (!cf || (code !== 'ADDING' && code !== 'DAY_LIMIT_REACHED')) return serverState();
  const pause = pausedUntil(cf);
  if (pause && pause.until.getTime() > now.getTime()) {
    const when = fmtResume(pause.until);
    return {
      code: 'LIMITED',
      tone: CF_STATES.LIMITED.tone,
      label: `Instagram limited – paused until ${when}`,
      text: pause.text
        ? `Instagram limited ('${pause.text}') – paused until ${when}`
        : `Instagram limited – paused until ${when}`,
      until: pause.until,
    };
  }
  if (pause) return serverState(); // the pause is over: the server's state again
  const blocked = limitedToday(cf, dayLabel(0, now));
  if (blocked !== null) {
    const r = splitStamp(cf.lastReport);
    const time = r && r.at ? r.at.slice(11) : null;
    return {
      code: 'LIMITED',
      ...CF_STATES.LIMITED,
      // the server's own alert wording (CloseFriendsStats.apply); the resume time is not in this line
      text: `Instagram limited this action ('${blocked}')${time ? ` at ${time}` : ''} – paused until tomorrow; nothing to do`,
    };
  }
  return serverState();
}

/**
 * The day-by-day "Close Friends added" history for the selected range, from the /stats fields.
 * @param statsRaw the GET /uiapi/slots/{slot}/stats body (flat, or under `stats`), or undefined
 * @returns {{ sent:boolean, present:boolean, day0, day0Known, today, series:number[], days:string[], rows, bars,
 *             weekly, total:number|null, avg:number|null, avgDays:number, partial:boolean, allZero:boolean }}
 *   sent: the server sent the fields (a server with Close Friends ON); present: at least one day is recorded.
 */
export function closeFriendsHistory(statsRaw, range, now = new Date()) {
  const src = (statsRaw && (statsRaw.stats || statsRaw.igAccStats)) || statsRaw || {};
  const today = dayLabel(0, now);
  const sent = has(src, 'closeFriendsAddedThisDaySticky') || has(src, 'closeFriendsAddedInPreviousDays');
  const todayKnown = num(src.closeFriendsAddedThisDaySticky) !== null;
  const csvLen = parseHistoryCsv(src.closeFriendsAddedInPreviousDays).length;
  const present = sent && (todayKnown || csvLen > 0);
  const resetDay = isoDay(src.lastResetDateTimeDays);
  const day0 = resetDay || today;
  const empty = {
    sent, present: false, day0, day0Known: resetDay !== null, today, series: [], days: [], rows: [], bars: [],
    weekly: range >= WEEKLY_FROM_RANGE, total: null, avg: null, avgDays: 0, partial: false, allZero: false,
  };
  if (!present) return empty;
  const series = dailySeries(src.closeFriendsAddedThisDaySticky, src.closeFriendsAddedInPreviousDays);
  const count = Math.min(range, series.length);
  const days = [];
  for (let k = count - 1; k >= 0; k--) days.push(shiftDay(day0, k));
  const partialDay = day0 === today ? day0 : null;
  const rows = days.map((date) => {
    const v = series[daysBetween(day0, date)];
    return { date, partial: date === partialDay, [CF_KEY]: v === undefined ? null : v };
  });
  const weekly = range >= WEEKLY_FROM_RANGE;
  const bars = actionBars(rows, CF_KEY, weekly, [CF_KEY]);
  const vals = rows.filter((r) => r[CF_KEY] !== null);
  const full = vals.filter((r) => !r.partial);
  return {
    sent,
    present: true,
    day0,
    day0Known: resetDay !== null,
    today,
    series,
    days,
    rows,
    bars,
    weekly,
    total: vals.length ? vals.reduce((a, r) => a + r[CF_KEY], 0) : null,
    avg: full.length ? full.reduce((a, r) => a + r[CF_KEY], 0) / full.length : null,
    avgDays: full.length,
    partial: vals.some((r) => r.partial),
    allZero: vals.length > 0 && vals.every((r) => r[CF_KEY] === 0),
  };
}

/** The list size as a share of the cap (0..1), or null when either is not known. */
export function capShare(cf) {
  if (!cf || cf.listSize === null || cf.cap === null || !(cf.cap > 0)) return null;
  return Math.max(0, Math.min(1, cf.listSize / cf.cap));
}
